import React, { useEffect } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.65);
  backdrop-filter: blur(2px);
  z-index: 80;
  display: flex;
  justify-content: flex-end;
`;

const Drawer = styled.div`
  width: min(680px, 95vw);
  height: 100vh;
  background: #0f172a;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem 1.75rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const Title = styled.div`
  color: #f8fafc;
  font-size: 1.15rem;
  font-weight: 700;

  .sub {
    display: block;
    color: #94a3b8;
    font-size: 0.78rem;
    font-weight: 400;
    margin-top: 0.3rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  border-radius: 6px;
  padding: 0.4rem 0.9rem;
  cursor: pointer;

  &:hover { color: white; border-color: #38bdf8; }
`;

const Notice = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  display: flex;
  gap: 0.85rem;
  color: #cbd5e1;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.35);
  border-radius: 10px;
  line-height: 1.55;

  svg {
    flex: 0 0 auto;
    color: #fbbf24;
    margin-top: 0.1rem;
  }

  strong { color: #f8fafc; }
  p { margin: 0.35rem 0 0; color: #94a3b8; }
`;

interface Props {
  loanId: number;
  loanName?: string;
  onClose: () => void;
}

/**
 * Phase-1 fail-closed view. A forecast cannot be displayed until contractual
 * payment dates/amortization ladders and an authoritative forward-rate path
 * can be combined with the portfolio's settled IBOR holding.
 */
const LoanForwardForecastDrawer: React.FC<Props> = ({ loanId, loanName, onClose }) => {
  useEffect(() => {
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <Backdrop onClick={onClose}>
      <Drawer onClick={(event) => event.stopPropagation()}>
        <Header>
          <Title>
            Cash Flow Forecast — Phase 2
            <span className="sub">{loanName || `Loan #${loanId}`}</span>
          </Title>
          <CloseButton onClick={onClose}>Close ✕</CloseButton>
        </Header>

        <Notice role="status">
          <AlertTriangle size={22} />
          <div>
            <strong>Forecast unavailable in Phase 1</strong>
            <p>
              No projection was generated. IBOR provides the current portfolio exposure, but
              authoritative contractual payment dates, amortization ladders, and forward rate
              fixings are not yet available together. Showing a schedule would require synthetic
              assumptions.
            </p>
          </div>
        </Notice>
      </Drawer>
    </Backdrop>
  );
};

export default LoanForwardForecastDrawer;
